import { ReactElement, useEffect, useMemo } from "react";
import { OriginalRevenueOperationalMetricValidKey, VersionType } from "../../../../__generated__/generated_types";
import { IVZHotTableRow } from "../../../../components/vz-ui/vz-hot-table/VZHotTable";
import { useUTSVOperationalMetrics } from "../hooks/useUTSVOperationalMetrics";
import { Property } from "../../../../contexts/properties/logic";
import { VZHotTableConfig } from "../../../../components/vz-ui/vz-hot-table/vz-hot-table-config";
import { buildUTSVTableConfig, buildUTSVTableData } from "../utils/operational-metric-helpers";
import OperationalVZHotTable from "../OperationalVZHotTable";
import {getMonths} from "../../../../utils/months-helper";


interface IRenewalRateManualEntryTableProps {
    property: Property;
    budgetYear: number;
    year: number;
    versionType: VersionType;
    onLastSaved: (lastSaved: Date) => void;
    onSavingValues: (saving: boolean) => void;
}

export default function RenewalRateManualEntryTable({ property, year, versionType, onLastSaved, onSavingValues, budgetYear }: IRenewalRateManualEntryTableProps): ReactElement {
    const {
        fetchMetricData,
        metricData,
        metricDataLoading,
        forecastLocks,
        lastSaved,
        savingValues,
        upsertValues,
    } = useUTSVOperationalMetrics({
        property,
        year: year,
        versionType,
    });

    useEffect(() => {
        onSavingValues(savingValues);
    }, [savingValues]);

    useEffect(() => {
        onLastSaved(lastSaved);
    }, [lastSaved]);

    useEffect(() => {
        fetchMetricData();
    }, [versionType]);

    const {tableData, tableConfig} = useMemo<{tableData: IVZHotTableRow[], tableConfig: VZHotTableConfig}>(() => {
        if (!forecastLocks?.forecastLocks || !metricData || !property) {
            return {
                tableData: [],
                tableConfig: new VZHotTableConfig(),
            };
        }

        const tableData = buildUTSVTableData(
            metricData,
            OriginalRevenueOperationalMetricValidKey.RenewalRate,
            versionType,
            property,
        );
        const tableConfig = buildUTSVTableConfig(
            tableData,
            versionType == VersionType.Budget ? forecastLocks?.forecastLocks?.property.budgetLocked : forecastLocks?.forecastLocks?.property.reforecastLocked,
            versionType,
            property.reforecastStartMonthIndex,
            "currency",
            "avg",
        );

        return {tableData, tableConfig};
    }, [metricData, forecastLocks?.forecastLocks]);

    return (
        <OperationalVZHotTable
            data={tableData}
            colWidths={[80]}
            colHeaders={["", ...getMonths(property), "Average"]}
            fixedRowsTop={1}
            fixedRowsBottom={tableData.filter(x => x.rowType == "historical").length}
            isSearchable
            isUndoRedoable
            tableConfig={tableConfig}
            isDataLoading={metricDataLoading}
            summaryColumns={[
                {
                    col: 13,
                    summaryFunction: "avg",
                },
            ]}
            onChange={(changes) => upsertValues(tableData, changes, budgetYear, OriginalRevenueOperationalMetricValidKey.RenewalRate)}
        />
    );
}
